import {
    Box,
    Button,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3"
import log from "loglevel"
import { ChangeEvent, useId, useMemo, useState } from "react"
import { DataReceiptsOrder, useFindDataReceipts } from "../../../api/DataReceiptApi"
import { BackButton } from "../../../components/BackButton"
import { NexPageContainer } from "../../../components/NexPageContainer"
import { NexPageSize, nexDefaultPagination, nexPageSizes } from "../../../components/Table/pageable-table/NexPageSize"
import { PagingFilters } from "../../../components/Table/pageable-table/PagingFilters"
import { Title } from "../../../components/Typography/Typography"
import { useNexusNavigate } from "../../../hooks/useNexusNavigate"
import { useDataReceiptsSearchFilters } from "../../../hooks/useNexusParams"
import { useTitle } from "../../../hooks/useTitle"
import { Order } from "../../../models/SortOrder"
import { OfficeId } from "../../../store/types/MissionsTypes"
import { prettyJson } from "../../../tools"
import { keysForStringEnum } from "../../../utils/EnumUtils"
import { TimeUtilsDateFns } from "../../../utils/TimeUtils"
import { DataReceiptSearchFitlersAndPaging } from "./DataReceiptSearchFilters"
import { useDebouncedDataReceiptTableSearchField } from "./DataReceiptsTableFilterDebounce"

export const DataReceiptsViewTable = () => {
    // Simple Hooks
    useTitle("Data Receipts")
    const nexusNavigate = useNexusNavigate()
    const {
        page,
        page_size,
        order,
        ordering,
        office,
        mission_name,
        project_name_or_number,
        source,
        tracking_number,
        received_start_date,
        received_end_date,
    } = useDataReceiptsSearchFilters()
    const officeLabelId = useId()

    // Setup Composite Values
    const pageWithDefault = page ?? nexDefaultPagination.page
    const pageSizeWithDefault = page_size ?? nexDefaultPagination.pageSize
    const orderWithDefault = order ?? Order.desc
    const orderByWithDefault = ordering ?? DataReceiptsOrder.DATE_RECEIVED
    const currentFilters: DataReceiptSearchFitlersAndPaging = {
        page: pageWithDefault,
        page_size: pageSizeWithDefault,
        order: orderWithDefault,
        ordering: orderByWithDefault,
        office,
        mission_name: mission_name?.length === 0 ? undefined : mission_name,
        project_name_or_number: project_name_or_number?.length === 0 ? undefined : project_name_or_number,
        source: source?.length === 0 ? undefined : source,
        tracking_number: tracking_number?.length === 0 ? undefined : tracking_number,
        received_start_date,
        received_end_date,
    }

    const [missionNameSource, setMissionNameSource] = useState<string | undefined>(currentFilters.mission_name)
    const [projectNameSource, setProjectNameSource] = useState<string | undefined>(
        currentFilters.project_name_or_number,
    )
    const [inputSource, setInputSource] = useState<string | undefined>(currentFilters.source)
    const [trackingNumberSource, setTrackingNumberSource] = useState<string | undefined>(currentFilters.tracking_number)

    const startDateDate =
        received_start_date == null ? undefined : TimeUtilsDateFns.getDateFromFormattedDay(received_start_date)
    const endDateDate =
        received_end_date == null ? undefined : TimeUtilsDateFns.getDateFromFormattedDay(received_end_date)

    log.info(`Current filters in table: ${prettyJson(currentFilters)}`)

    // Filter Debounce Functions
    const debouncedMissionName = useDebouncedDataReceiptTableSearchField(
        "mission_name",
        mission_name?.trim(),
        currentFilters,
    )
    const debouncedProjectName = useDebouncedDataReceiptTableSearchField(
        "project_name_or_number",
        project_name_or_number?.trim(),
        currentFilters,
    )
    const debouncedSource = useDebouncedDataReceiptTableSearchField("source", source?.trim(), currentFilters)
    const debouncedTrackingNumber = useDebouncedDataReceiptTableSearchField(
        "tracking_number",
        tracking_number?.trim(),
        currentFilters,
    )

    const updateOffice = (event: SelectChangeEvent) => {
        const office = event.target.value === "" ? undefined : (event.target.value as OfficeId)
        const searchWith: DataReceiptSearchFitlersAndPaging = {
            ...currentFilters,
            page: 0,
            office: office,
        }
        nexusNavigate.searchDataReceipts(searchWith)
    }

    const updateReceivedStartDate = (startDate: Date | null) => {
        const searchWith: DataReceiptSearchFitlersAndPaging = {
            ...currentFilters,
            page: 0,
            received_start_date: startDate == null ? undefined : TimeUtilsDateFns.formatDate(startDate),
        }
        nexusNavigate.searchDataReceipts(searchWith)
    }
    const updateReceivedEndDate = (endDate: Date | null) => {
        const searchWith: DataReceiptSearchFitlersAndPaging = {
            ...currentFilters,
            page: 0,
            received_end_date: endDate == null ? undefined : TimeUtilsDateFns.formatDate(endDate),
        }
        nexusNavigate.searchDataReceipts(searchWith)
    }

    const clearFiltersValue: PagingFilters<DataReceiptsOrder> = {
        page: pageWithDefault,
        page_size: pageSizeWithDefault,
        order: orderWithDefault,
        ordering: orderByWithDefault,
    }
    const disableClearFilters = JSON.stringify(clearFiltersValue) === JSON.stringify(currentFilters)

    const clearFilters = () => {
        setMissionNameSource(undefined)
        setProjectNameSource(undefined)
        setInputSource(undefined)
        setTrackingNumberSource(undefined)
        nexusNavigate.searchDataReceipts()
    }

    // Tanstack Query Hooks
    const dataReceiptsResponse = useFindDataReceipts(currentFilters)

    // Process Fetched Data
    const dataReceipts = dataReceiptsResponse.data ?? { count: 0, results: [] }
    const tableRowData = useMemo(
        () =>
            dataReceipts.results.map((data) => {
                return {
                    dataReceiptId: data.receipt.id,
                    mission: data.mission_shipping_info?.mission?.mission_name,
                    project: data.mission_shipping_info?.mission?.project,
                    trackingNumber: data.mission_shipping_info?.shipping_number,
                    platform:
                        data.mission_shipping_info?.mission?.platform == null
                            ? undefined
                            : `${data.mission_shipping_info.mission.platform.platform_type}: ${data.mission_shipping_info.mission.platform.platform_name}`,
                    received_by: data.receipt.received_by_name,
                    dateReceived: data.receipt.date_received,
                    source: data.mission_shipping_info?.delivery_drive_name,
                    office: data.receipt.office,
                }
            }),
        [dataReceipts],
    )

    // Handler Functions
    const handlePageChange = (page: number) => {
        nexusNavigate.searchDataReceipts({
            ...currentFilters,
            page,
        })
    }
    const handleRowsPerPageChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        nexusNavigate.searchDataReceipts({
            ...currentFilters,
            page_size: Number(event.target.value) as NexPageSize,
        })
    }
    const handleOrderChange = () => {
        log.info(`Swap sort from ${orderWithDefault} to ${order === Order.asc ? Order.desc : Order.asc}`)
        nexusNavigate.searchDataReceipts({
            ...currentFilters,
            order: orderWithDefault === Order.asc ? Order.desc : Order.asc,
        })
    }
    return (
        <NexPageContainer py={4}>
            <Title>Data Receipts</Title>
            <Paper sx={{ marginX: "auto", my: 2 }}>
                <Box display={"flex"} gap={2} p={2} alignItems={"center"}>
                    <TextField
                        label={"Mission Name"}
                        variant={"outlined"}
                        value={missionNameSource ?? ""}
                        onChange={(event) => {
                            debouncedMissionName(event.target.value)
                            setMissionNameSource(event.target.value)
                        }}
                        sx={{ width: 160 }}
                    />
                    <TextField
                        label={"Project Name or Number"}
                        variant={"outlined"}
                        value={projectNameSource ?? ""}
                        onChange={(event) => {
                            debouncedProjectName(event.target.value)
                            setProjectNameSource(event.target.value)
                        }}
                        sx={{ width: 220 }}
                    />
                    <TextField
                        label={"Source"}
                        variant={"outlined"}
                        value={inputSource ?? ""}
                        onChange={(event) => {
                            debouncedSource(event.target.value)
                            setInputSource(event.target.value)
                        }}
                        sx={{ width: 160 }}
                    />
                    <TextField
                        label={"Tracking Number"}
                        variant={"outlined"}
                        value={trackingNumberSource ?? ""}
                        onChange={(event) => {
                            debouncedTrackingNumber(event.target.value)
                            setTrackingNumberSource(event.target.value)
                        }}
                        sx={{ width: 160 }}
                    />
                    <FormControl sx={{ width: 120 }}>
                        <InputLabel id={officeLabelId}>Office</InputLabel>
                        <Select
                            labelId={officeLabelId}
                            id="demo-simple-select"
                            value={office ?? ""}
                            label={"office"}
                            onChange={updateOffice}
                        >
                            <MenuItem value={""}>None</MenuItem>
                            {keysForStringEnum(OfficeId).map((officeId) => (
                                <MenuItem key={officeId} value={officeId}>
                                    {officeId}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Start Date"
                            sx={{ width: 200 }}
                            value={startDateDate ?? null}
                            onChange={updateReceivedStartDate}
                            slotProps={{ field: { clearable: true } }}
                        />
                    </LocalizationProvider>

                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            sx={{ width: 200 }}
                            label="End Date"
                            value={endDateDate ?? null}
                            onChange={updateReceivedEndDate}
                            slotProps={{ field: { clearable: true } }}
                        />
                    </LocalizationProvider>
                    <Button
                        sx={{ marginLeft: "auto" }}
                        color={"inherit"}
                        onClick={clearFilters}
                        disabled={disableClearFilters}
                    >
                        Clear Filters
                    </Button>
                </Box>
                <TableContainer
                    sx={{
                        td: { whiteSpace: "nowrap" },
                        overflowY: "auto",
                    }}
                >
                    <Table stickyHeader={true} size="small">
                        <TableHead>
                            <TableRow sx={{ "& .td": { padding: 0 } }}>
                                <TableCell>Data Receipt</TableCell>
                                <TableCell>Mission</TableCell>
                                <TableCell>Project</TableCell>
                                <TableCell sortDirection={order ?? Order.desc}>
                                    <TableSortLabel
                                        active={true}
                                        direction={order ?? Order.desc}
                                        onClick={handleOrderChange}
                                    >
                                        Received
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Source</TableCell>
                                <TableCell>Tracking Number</TableCell>
                                <TableCell>Office</TableCell>
                                <TableCell>Platform</TableCell>
                                <TableCell>Logged By</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dataReceiptsResponse.isFetching && (
                                <TableRow>
                                    <TableCell padding="none" colSpan={10}>
                                        <LinearProgress />
                                    </TableCell>
                                </TableRow>
                            )}
                            {dataReceipts.results.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={10}>
                                        <Typography>No Results on page {pageWithDefault + 1}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {tableRowData.map((row) => {
                                // TODO add ID for key
                                return (
                                    <TableRow key={crypto.randomUUID()}>
                                        <TableCell>
                                            <Button
                                                variant="outlined"
                                                onClick={() => nexusNavigate.dataReceiptEditor(`${row.dataReceiptId}`)}
                                            >
                                                #{row.dataReceiptId}
                                            </Button>
                                        </TableCell>
                                        <TableCell>{row.mission}</TableCell>
                                        <TableCell>
                                            {row.project != null && (
                                                <>
                                                    <Typography variant={"body2"}>
                                                        {row.project.project_number}
                                                    </Typography>
                                                    <Typography variant={"body2"}>
                                                        {row.project.project_short_name}
                                                    </Typography>
                                                </>
                                            )}
                                        </TableCell>
                                        <TableCell>{TimeUtilsDateFns.formatDate(new Date(row.dateReceived))}</TableCell>
                                        <TableCell>{row.source}</TableCell>
                                        <TableCell>{row.trackingNumber}</TableCell>
                                        <TableCell>{row.office}</TableCell>
                                        <TableCell>{row.platform}</TableCell>
                                        <TableCell>{row.received_by}</TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    showFirstButton={true}
                    showLastButton={true}
                    component={"div"}
                    rowsPerPageOptions={nexPageSizes}
                    count={dataReceipts.count ?? -1} // -1 allows for server side pagination for an unknown number of items
                    page={pageWithDefault}
                    rowsPerPage={pageSizeWithDefault}
                    onPageChange={(_, page) => handlePageChange(page)}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            </Paper>
            <BackButton />
        </NexPageContainer>
    )
}
